import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import type { Catalogs, RingBuilderClientApi, RingState } from "../../shared/api-contract";
import { useRingState } from "../../state/hooks";
import { CapsProvider } from "../editor/CapsProvider";
import { DraftProvider, useDraftController } from "../kit";

/**
 * Everything a Ring Builder tab needs. The viewer is the single source of
 * truth: a setter RPC returns the fresh `RingState`, `apply()` stores it, and
 * the UI re-renders from `state`. Never mirror viewer state locally.
 */
export interface RingCtx {
  /** The live merged state (band + optional engagement block). */
  state: RingState;
  catalogs: Catalogs;
  /** `client.ringBuilder` — the merged v37 facade. NOT `client.ring`. */
  r: RingBuilderClientApi;
  /** Run a setter and store the RingState it returns: `apply(r.setWidth(4))`. */
  apply: (p: Promise<RingState | null>) => Promise<void>;
  /** Re-pull the snapshot (after a fire-and-forget setter that returns void). */
  refresh: () => Promise<void>;
  /**
   * Manual draft batching, ref-counted. Sliders from the kit do this for you —
   * only reach for these when batching something that is not a slider drag.
   */
  beginDraft: () => void;
  endDraft: () => void;
  /** `state.engagement?.enabled` — the head assembly is attached. */
  engagementOn: boolean;
}

const Ctx = createContext<RingCtx | null>(null);

/**
 * Mounts the Ring Builder state, the ref-counted draft controller (so every kit
 * slider batches automatically) and `CapsProvider` — tabs may call `useCaps()`
 * without wrapping anything themselves.
 *
 * Renders `fallback` until the first `getRingState()` lands.
 */
export function RingProvider({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const { state, apply, refresh, client, catalogs } = useRingState();
  const r = client.ringBuilder;
  const draft = useDraftController(() => void r.beginDraft(), () => void r.endDraft());

  const value = useMemo<RingCtx | null>(
    () => (state ? {
      state,
      catalogs,
      r,
      apply,
      refresh,
      beginDraft: draft.begin,
      endDraft: draft.end,
      engagementOn: state.engagement?.enabled ?? false,
    } : null),
    [state, catalogs, r, apply, refresh, draft],
  );

  if (!value) return <>{fallback}</>;

  return (
    <CapsProvider>
      <DraftProvider controller={draft}>
        <Ctx.Provider value={value}>{children}</Ctx.Provider>
      </DraftProvider>
    </CapsProvider>
  );
}

/**
 * Mounts a FIXED ring context (no viewer round trip, no draft controller) —
 * for static rendering in the host-side checks (`check-ring-designs-w1`), where
 * there is no iframe to fetch a state from. Needs a `ViewerProvider` above it
 * (for `CapsProvider`).
 */
export function RingStaticProvider({ value, children }: { value: RingCtx; children: ReactNode }) {
  return (
    <CapsProvider>
      <Ctx.Provider value={value}>{children}</Ctx.Provider>
    </CapsProvider>
  );
}

export function useRing(): RingCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useRing must be used within a RingProvider");
  return ctx;
}
